import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { TFunction } from "i18next";

import { EdictTransferOutcome, EdictTransferResponse } from "@/api";
import { useTranslation } from "@/translations";

interface Props {
  result: EdictTransferResponse;
  onClose: () => void;
}

interface ResultLine {
  id: string;
  text: string;
  /** Whether the line reports something the transfer wrote, as opposed to something it left out. */
  written: boolean;
}

/**
 * The confirmation of a "§" transfer: one line per thing the transfer wrote, and one per thing it left
 * out together with the reason. Every line is listed either way — "no Tagsatzung in this Edikt" is as much
 * of an answer as "Tagsatzung eingetragen", and a staff member who sees neither cannot tell the two apart.
 */
export function EdictTransferResultDialog({ result, onClose }: Props) {
  const { t } = useTranslation();

  const lines = buildLines(result, t);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ p: 5, pb: 2 }} variant="h1">
        {t("edicts.transferResult.title")}
      </DialogTitle>
      <DialogContent sx={{ p: 5, pt: 0 }}>
        <List dense disablePadding>
          {lines.map((line) => (
            <ListItem key={line.id} disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>
                {line.written ? (
                  <CheckCircleOutlineIcon color="success" fontSize="small" />
                ) : (
                  <InfoOutlinedIcon color="info" fontSize="small" />
                )}
              </ListItemIcon>
              <ListItemText primary={line.text} />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions sx={{ p: 5, pt: 0, justifyContent: "flex-start" }}>
        <Button variant="contained" onClick={onClose}>
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function buildLines(result: EdictTransferResponse, t: TFunction): ResultLine[] {
  const lines: ResultLine[] = [
    {
      id: "proceeding",
      text: result.proceedingCreated
        ? t("edicts.transferResult.proceedingCreated")
        : t("edicts.transferResult.proceedingUpdated"),
      written: true,
    },
    openingDateLine(result, t),
    {
      id: "fileNumber",
      text: result.fileNumberSet
        ? t("edicts.transferResult.fileNumberSet")
        : t("edicts.transferResult.fileNumberSkipped"),
      written: result.fileNumberSet,
    },
    {
      id: "court",
      text: result.courtSet ? t("edicts.transferResult.courtSet") : t("edicts.transferResult.courtSkipped"),
      written: result.courtSet,
    },
  ];

  // Only reported when they happened: an edict states a quota or it does not, and a client without a fixed
  // counselor has no counseling type to set — neither is something the staff member has to act on.
  if (result.offeredQuotaSet) {
    lines.push({ id: "offeredQuota", text: t("edicts.transferResult.offeredQuotaSet"), written: true });
  }

  if (result.counselingTypeSet) {
    lines.push({ id: "counselingType", text: t("edicts.transferResult.counselingTypeSet"), written: true });
  }

  lines.push(
    hearingLine(result.hearing, t),
    hearingCaseNoteLine(result.hearingCaseNote, t),
    assignmentLine(result.assignment, t)
  );

  return lines;
}

/**
 * "Kept" and "the Edikt names none" are two different answers, and a staff member who reads the first
 * for an opening date the Edikt never carried goes looking for a date that is not there.
 */
function openingDateLine(result: EdictTransferResponse, t: TFunction): ResultLine {
  if (result.openingDateSet) {
    return { id: "openingDate", text: t("edicts.transferResult.openingDateSet"), written: true };
  }

  return result.openingDateInEdict
    ? { id: "openingDate", text: t("edicts.transferResult.openingDateKept"), written: false }
    : { id: "openingDate", text: t("edicts.transferResult.openingDateMissing"), written: false };
}

function hearingLine(outcome: EdictTransferOutcome, t: TFunction): ResultLine {
  switch (outcome) {
    case EdictTransferOutcome.Created:
      return { id: "hearing", text: t("edicts.transferResult.hearingCreated"), written: true };
    case EdictTransferOutcome.Updated:
      return { id: "hearing", text: t("edicts.transferResult.hearingUpdated"), written: true };
    case EdictTransferOutcome.HearingTimeMissing:
      return { id: "hearing", text: t("edicts.transferResult.hearingTimeMissing"), written: false };
    default:
      return { id: "hearing", text: t("edicts.transferResult.hearingMissing"), written: false };
  }
}

function hearingCaseNoteLine(outcome: EdictTransferOutcome, t: TFunction): ResultLine {
  switch (outcome) {
    case EdictTransferOutcome.Created:
      return { id: "hearingCaseNote", text: t("edicts.transferResult.hearingCaseNoteCreated"), written: true };
    case EdictTransferOutcome.Updated:
      return { id: "hearingCaseNote", text: t("edicts.transferResult.hearingCaseNoteUpdated"), written: true };
    case EdictTransferOutcome.HearingTimeMissing:
      return { id: "hearingCaseNote", text: t("edicts.transferResult.hearingCaseNoteTimeMissing"), written: false };
    case EdictTransferOutcome.HearingPassed:
      return { id: "hearingCaseNote", text: t("edicts.transferResult.hearingCaseNotePassed"), written: false };
    default:
      return { id: "hearingCaseNote", text: t("edicts.transferResult.hearingCaseNoteMissing"), written: false };
  }
}

function assignmentLine(outcome: EdictTransferOutcome, t: TFunction): ResultLine {
  switch (outcome) {
    case EdictTransferOutcome.Created:
      return { id: "assignment", text: t("edicts.transferResult.assignmentCreated"), written: true };
    case EdictTransferOutcome.Updated:
      return { id: "assignment", text: t("edicts.transferResult.assignmentUpdated"), written: true };
    case EdictTransferOutcome.NoCounselor:
      return { id: "assignment", text: t("edicts.transferResult.assignmentNoCounselor"), written: false };
    case EdictTransferOutcome.DeadlinePassed:
      return { id: "assignment", text: t("edicts.transferResult.assignmentDeadlinePassed"), written: false };
    default:
      return { id: "assignment", text: t("edicts.transferResult.assignmentMissing"), written: false };
  }
}
